import { ReactNode, useContext } from "react";
import { LoanApplicationStage } from "../../hooks/api/types";
import { StepName, SubStepName, stepConfig as defaultStepConfig } from ".";
import { StepperContext, StepperContextProvider } from "../../contexts";
import { useDpConfig } from "../../contexts/dpConfig";
import { DistributionPartnerSettings } from "../../contexts/dpConfig/types";

export const STAGE_STEP_NAME_MAPPING: {
  [k in LoanApplicationStage]: [string, number];
} = {
  // [LoanApplicationStage.REASON]: [StepName.LOAN_INITIAL, 0],
  [LoanApplicationStage.REASON]: [StepName.LOAN_REASONS, 0],
  [LoanApplicationStage.TYPE]: [StepName.LOAN_TYPE, 0],
  [LoanApplicationStage.BRANDING]: [StepName.BUSINESS_INFORMATION, 0],
  [LoanApplicationStage.CALCULATOR]: [StepName.BUSINESS_INFORMATION, 0],
  [LoanApplicationStage.BUSINESS_INFORMATION]: [
    StepName.BUSINESS_INFORMATION,
    0,
  ],
  [LoanApplicationStage.COMPANY_INFORMATION]: [
    StepName.BUSINESS_INFORMATION,
    2,
  ],
  [LoanApplicationStage.DOCUMENTS_UPLOAD]: [StepName.BUSINESS_INFORMATION, 4],
  [LoanApplicationStage.ONBOARDING]: [StepName.BUSINESS_INFORMATION, 0],
  [LoanApplicationStage.OWNER_INFORMATION]: [StepName.OWNERS_INFORMATION, 0],
  [LoanApplicationStage.SHAREHOLDER_INFORMATION]: [
    StepName.OWNERS_INFORMATION,
    2,
  ],
  [LoanApplicationStage.FINANCIAL_INFORMATION]: [
    StepName.FINANCIAL_INFORMATION,
    0,
  ],
  [LoanApplicationStage.VAT_STATEMENTS]: [StepName.FINANCIAL_INFORMATION, 3],
  [LoanApplicationStage.GENERIC_DOCUMENTS]: [StepName.FINANCIAL_INFORMATION, 4],
  [LoanApplicationStage.MORE_INFORMATION]: [StepName.FINANCIAL_INFORMATION, 5],
  [LoanApplicationStage.REVIEW]: [StepName.APPLICATION_REVIEW, 0],
  [LoanApplicationStage.BANK_STATEMENTS]: [StepName.FINANCIAL_INFORMATION, 1],
  [LoanApplicationStage.KYB]: [StepName.BUSINESS_INFORMATION, 0],
  [LoanApplicationStage.KYC]: [StepName.OWNERS_INFORMATION, 3],
  [LoanApplicationStage.SUCCESS]: [StepName.SUCCESS, 0],
};

const createConditionalStepConfig = (
  settings: DistributionPartnerSettings,
  shouldHideFinancialInformation: boolean
) => {
  return defaultStepConfig
    .filter((step) => {
      // Exclude the entire Financial Information step if shouldHideFinancialInformation is true
      if (
        step.name === StepName.FINANCIAL_INFORMATION &&
        shouldHideFinancialInformation
      ) {
        return false;
      }
      return true;
    })
    .map((step) => {
      step.subSteps = step.subSteps.filter((subStep) => {
        // Skip bank statement-related substeps if disabled
        if (
          settings.disableBankStatementsUpload &&
          (subStep.name === SubStepName.FINANCE_INFORMATION_SELECT ||
            subStep.name === SubStepName.FINANCE_INFORMATION)
        ) {
          return false;
        }

        // Skip sales statement-related substep if disabled
        if (
          settings.disableSalesStatementsUpload &&
          subStep.name === SubStepName.UPLOAD_SALES_STATEMENT
        ) {
          return false;
        }
        if (
          settings.disableVATStatementsUpload &&
          subStep.name === SubStepName.VAT_INFORMATION
        ) {
          return false;
        }
        if (
          settings.disableSalesInvoiceUpload &&
          subStep.name === SubStepName.UPLOAD_SALES_INVOICE
        ) {
          return false;
        }
        if (
          settings.hideLoanReasonsScreen &&
          subStep.name === SubStepName.LOAN_REASON
        ) {
          return false;
        }
        if (
          settings.hideProductSelectionPage &&
          subStep.name === SubStepName.LOAN_TYPE
        ) {
          return false;
        }
        if (
          settings.hideEligibilityScreen &&
          subStep.name === SubStepName.COMPANY_INFORMATION_START_SCREEN
        ) {
          return false;
        }

        return true;
      });

      return step;
    });
};

type Props = {
  children: ReactNode;
};

export const LoanApplicationStepper: React.FC<Props> = ({ children }) => {
  const {
    state: { settings, shouldHideFinancialInformation },
  } = useDpConfig();

  const stepConfig = createConditionalStepConfig(
    settings,
    shouldHideFinancialInformation
  );

  return (
    <StepperContextProvider stepConfig={stepConfig}>
      {children}
    </StepperContextProvider>
  );
};

export const useStepper = () => useContext(StepperContext);
